import {
  Box,
  Button,
  MenuItem,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import ReadOnlyTooltip from "components/common/ReadOnlyTooltip";
import PaginationControls from "components/fileStorage/PaginationControls";
import { MANAGEMENT_TABS_SX } from "components/management/managementTheme";
import ListToolbar from "components/secureExchange/ListToolbar";
import ProjectDialogs, {
  type ProjectAction,
} from "components/secureExchange/ProjectDialogs";
import ProjectTable, {
  type ProjectSort,
} from "components/secureExchange/ProjectTable";
import SecureExchangeBreadcrumbs from "components/secureExchange/SecureExchangeBreadcrumbs";
import SecureExchangeHeader from "components/secureExchange/SecureExchangeHeader";
import SecureExchangeShell from "components/secureExchange/SecureExchangeShell";
import SecureFileTable from "components/secureExchange/SecureFileTable";
import SecureUploadDialog from "components/secureExchange/SecureUploadDialog";
import {
  type FileSort,
  nextFileSort,
} from "components/secureExchange/fileModel";
import { ClientIcon } from "components/secureExchange/icons";
import { useDebouncedValue } from "components/secureExchange/useDebouncedValue";
import { useFileActions } from "components/secureExchange/useFileActions";
import { useSecureExchangePaths } from "components/secureExchange/useSecureExchangePaths";
import {
  type SecureProjectStatus,
  useGetSecureClientQuery,
  useGetSecureFilesQuery,
  useGetSecureProjectsQuery,
} from "features/secureUploads/secureUploads";
import { usePermissions } from "hooks/usePermissions";
import { useViewAs } from "hooks/useViewAs";
import { ACTIONS } from "utils/access";

type ClientTab = "projects" | "files" | "deleted";

const PAGE_SIZE = 25;

function Pager({
  total,
  page,
  limit,
  onPage,
  onLimit,
}: {
  total: number;
  page: number;
  limit: number;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
}) {
  if (total === 0) return null;
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <PaginationControls
      currentLimit={limit}
      onLimitChange={onLimit}
      totalItems={total}
      currentPage={page}
      totalPages={pages}
      onPageChange={onPage}
      size="small"
      hasNextPage={page < pages}
    />
  );
}

interface TabActions {
  /** New Project and Upload New File, as the tab shows them. */
  buttons: React.ReactNode;
}

function ProjectsTab({
  clientId,
  canOrganize,
  readOnly,
  buttons,
  onAction,
}: TabActions & {
  clientId: number;
  canOrganize: boolean;
  readOnly: boolean;
  onAction: (action: ProjectAction) => void;
}) {
  const paths = useSecureExchangePaths();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [status, setStatus] = useState<SecureProjectStatus | "">("");
  const [sort, setSort] = useState<ProjectSort | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, isFetching } = useGetSecureProjectsQuery({
    client_id: clientId,
    status: status || undefined,
    search: search || undefined,
    ordering: sort
      ? `${sort.direction === "desc" ? "-" : ""}${sort.column}`
      : undefined,
    page,
    page_size: limit,
  });

  return (
    <>
      <ListToolbar
        search={searchInput}
        onSearch={(value) => {
          setSearchInput(value);
          setPage(1);
        }}
        placeholder="Search Projects"
        filters={
          <Select
            size="small"
            value={status}
            displayEmpty
            onChange={(event) => {
              setStatus(event.target.value as SecureProjectStatus | "");
              setPage(1);
            }}
            inputProps={{ "aria-label": "Project status" }}
            sx={{ minWidth: 150 }}
          >
            <MenuItem value="">All Statuses</MenuItem>
            <MenuItem value="open">Open</MenuItem>
            <MenuItem value="closed">Closed</MenuItem>
          </Select>
        }
        actions={buttons}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: isFetching && !isLoading ? 0.6 : 1,
        }}
      >
        <ProjectTable
          projects={data?.results ?? []}
          loading={isLoading}
          sort={sort}
          onSort={(column) => {
            setSort((current) =>
              current?.column === column
                ? {
                    column,
                    direction: current.direction === "asc" ? "desc" : "asc",
                  }
                : { column, direction: column === "name" ? "asc" : "desc" },
            );
            setPage(1);
          }}
          empty={
            search || status
              ? "No projects match."
              : canOrganize
                ? "No projects yet. Start one with New Project."
                : "You are not on any project yet."
          }
          projectPath={(project) => paths.project(clientId, project.id)}
          canOrganize={canOrganize}
          readOnly={readOnly}
          onAction={onAction}
        />
      </Box>
      <Pager
        total={data?.count ?? 0}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={(next) => {
          setLimit(next);
          setPage(1);
        }}
      />
    </>
  );
}

function FilesTab({
  clientId,
  deleted,
  readOnly,
  canOrganize,
  buttons,
}: TabActions & {
  clientId: number;
  deleted: boolean;
  readOnly: boolean;
  canOrganize: boolean;
}) {
  const actions = useFileActions();
  const [searchInput, setSearchInput] = useState("");
  const search = useDebouncedValue(searchInput.trim());
  const [sort, setSort] = useState<FileSort | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const { data, isLoading, isFetching } = useGetSecureFilesQuery({
    tab: deleted ? "deleted" : "live",
    location: deleted ? undefined : "individual",
    client: clientId,
    search: search || undefined,
    sort_by: sort?.column ?? null,
    sort_order: sort?.direction ?? null,
    page,
    page_size: limit,
  });

  return (
    <>
      <ListToolbar
        search={searchInput}
        onSearch={(value) => {
          setSearchInput(value);
          setPage(1);
        }}
        placeholder={deleted ? "Search deleted files" : "Search files"}
        actions={buttons}
      />
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          opacity: isFetching && !isLoading ? 0.6 : 1,
        }}
      >
        <SecureFileTable
          files={data?.results ?? []}
          loading={isLoading}
          deleted={deleted}
          sort={sort}
          onSort={(column) => {
            setSort((current) => nextFileSort(current, column));
            setPage(1);
          }}
          empty={
            search
              ? "No files match."
              : deleted
                ? "No deleted files."
                : "No files here yet."
          }
          readOnly={readOnly}
          canOrganize={canOrganize}
          onAction={actions.run}
        />
      </Box>
      <Pager
        total={data?.count ?? 0}
        page={page}
        limit={limit}
        onPage={setPage}
        onLimit={(next) => {
          setLimit(next);
          setPage(1);
        }}
      />
      {actions.dialogs}
    </>
  );
}

/** A client's page: its projects, its Individual Files and, for Manage, the
 * files deleted there. */
export default function SecureExchangeClientPage() {
  const params = useParams<{ clientId: string }>();
  const clientId = Number(params.clientId);
  const paths = useSecureExchangePaths();
  const { readOnly } = useViewAs();
  const { sees } = usePermissions();
  const canOrganize = sees(ACTIONS.SECURE_EXCHANGE_ORGANIZE);
  const seesDeleted = sees(ACTIONS.SECURE_EXCHANGE_DELETED);
  const [searchParams, setSearchParams] = useSearchParams();
  const [action, setAction] = useState<ProjectAction | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const {
    data: client,
    isLoading,
    isError,
  } = useGetSecureClientQuery(clientId, { skip: !clientId });
  const missing = !clientId || isError;

  const requested = searchParams.get("tab");
  const tab: ClientTab =
    requested === "files"
      ? "files"
      : requested === "deleted" && seesDeleted
        ? "deleted"
        : "projects";
  const openTab = (next: ClientTab) =>
    setSearchParams(next === "projects" ? {} : { tab: next }, {
      replace: true,
    });

  const newProject = canOrganize ? (
    <ReadOnlyTooltip active={readOnly}>
      <Button
        variant="outlined"
        disabled={readOnly}
        onClick={() => setAction({ kind: "create", clientId })}
      >
        New Project
      </Button>
    </ReadOnlyTooltip>
  ) : null;
  const upload = (
    <ReadOnlyTooltip active={readOnly}>
      <Button
        variant="contained"
        disabled={readOnly}
        onClick={() => setUploadOpen(true)}
      >
        Upload New File
      </Button>
    </ReadOnlyTooltip>
  );
  const buttons = (
    <>
      {newProject}
      {upload}
    </>
  );

  return (
    <SecureExchangeShell
      title={client ? `${client.name} - Secure Exchange` : "Secure Exchange"}
      breadcrumbs={
        <SecureExchangeBreadcrumbs
          items={[paths.home, { label: client?.name ?? "" }]}
        />
      }
      header={
        <SecureExchangeHeader
          icon={<ClientIcon />}
          title={missing ? "Client not found" : client?.name}
          loading={isLoading}
          counters={
            missing
              ? []
              : [
                  { label: "Open Projects", value: client?.open_projects },
                  { label: "Files", value: client?.files },
                  { label: "Contributors", value: client?.contributors },
                ]
          }
        />
      }
    >
      {missing ? (
        <Typography sx={{ p: 3 }} color="text.secondary">
          This client is not in your Secure Exchange.
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              px: { xs: 1, sm: 2 },
              borderBottom: "1px solid",
              borderColor: "divider",
              flexShrink: 0,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_event, next: ClientTab) => openTab(next)}
              variant="scrollable"
              allowScrollButtonsMobile
              sx={MANAGEMENT_TABS_SX}
            >
              <Tab value="projects" label="Projects" />
              <Tab value="files" label="Individual Files" />
              {seesDeleted ? (
                <Tab value="deleted" label="Deleted Files" />
              ) : null}
            </Tabs>
          </Box>
          {tab === "projects" ? (
            <ProjectsTab
              key={`projects-${clientId}`}
              clientId={clientId}
              canOrganize={canOrganize}
              readOnly={readOnly}
              buttons={buttons}
              onAction={setAction}
            />
          ) : (
            <FilesTab
              key={`${tab}-${clientId}`}
              clientId={clientId}
              deleted={tab === "deleted"}
              readOnly={readOnly}
              canOrganize={canOrganize}
              buttons={tab === "deleted" ? null : buttons}
            />
          )}
        </>
      )}
      <ProjectDialogs action={action} onClose={() => setAction(null)} />
      <SecureUploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        clientId={clientId}
        pickPlace={tab === "projects"}
      />
    </SecureExchangeShell>
  );
}
